import { API_URL } from '@/config';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileText, Download, CheckCircle, XCircle, Clock, Eye, Trash2, Search, Filter, Calendar, Mail, Vote, Edit, Save, Upload, X, Bell, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

// Valid category pattern - only match values starting with number followed by underscore
const CATEGORY_PATTERN = /^(\d+)\._(.+)$/;

// Helper function to format category for display
const formatCategory = (value) => {
  if (!value) return null;
  const match = value.match(CATEGORY_PATTERN);
  if (match) {
    const num = match[1];
    const name = match[2].replace(/_/g, ' ').trim();
    // Return short version for table display
    return `${num}. ${name.length > 40 ? name.substring(0, 40) + '...' : name}`;
  }
  return value;
};

// Helper function to extract category from form responses
const extractCategory = (responses) => {
  if (!responses) return null;
  
  // First try direct field names
  if (responses.category && CATEGORY_PATTERN.test(responses.category)) {
    return formatCategory(responses.category);
  }
  if (responses.nominee_category && CATEGORY_PATTERN.test(responses.nominee_category)) {
    return formatCategory(responses.nominee_category);
  }
  
  // Search through all fields for category values (must match pattern like "1._xxx_award")
  for (const [key, value] of Object.entries(responses)) {
    if (typeof value === 'string' && CATEGORY_PATTERN.test(value)) {
      return formatCategory(value);
    }
  }
  return null;
};

// Helper function to extract photo from form responses  
const extractPhoto = (responses) => {
  if (!responses) return null;
  
  // First try direct field names
  const photoFields = ['photo', 'nominee_photo', 'image', 'profile_photo', 'picture'];
  for (const field of photoFields) {
    if (responses[field]) return responses[field];
  }
  
  // Search through all fields for image URLs
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  for (const [key, value] of Object.entries(responses)) {
    if (typeof value === 'string') {
      if (imageExtensions.some(ext => value.toLowerCase().endsWith(ext))) {
        return value;
      }
      if (value.includes('/uploads/') && imageExtensions.some(ext => value.toLowerCase().includes(ext))) {
        return value;
      }
    }
  }
  return null;
};

const AdminFormResponsesPage = () => {
  const { user } = useOutletContext();
  const [submissions, setSubmissions] = useState([]);
  const [events, setEvents] = useState([]);
  const [forms, setForms] = useState({}); // Store form definitions for field labels
  const [pollingEventIds, setPollingEventIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editFormFields, setEditFormFields] = useState([]); // Store form field definitions for editing
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0 });
  const [resendingId, setResendingId] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationData, setNotificationData] = useState({ subject: '', message: '' });
  
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedEvent && selectedEvent !== 'all') {
      loadEventSubmissions(selectedEvent);
    } else {
      loadAllSubmissions();
    }
  }, [selectedEvent]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [submissionsRes, eventsRes, formsRes] = await Promise.all([
        axios.get(`${API}/forms/submissions/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/forms`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSubmissions(submissionsRes.data);
      const eventsWithForms = eventsRes.data.filter(e => e.registration_form_id);
      setEvents(eventsWithForms);
      
      // Store forms indexed by form_id for quick lookup
      const formsMap = {};
      formsRes.data.forEach(form => {
        formsMap[form.form_id] = form;
      });
      setForms(formsMap);
      
      // Track polling-enabled event IDs
      const pollingIds = new Set(eventsRes.data.filter(e => e.polling_required).map(e => e.event_id));
      setPollingEventIds(pollingIds);
      
      // Extract unique categories from submissions
      extractCategories(submissionsRes.data);
      calculateStats(submissionsRes.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const extractCategories = (data) => {
    const categorySet = new Set();
    data.forEach(sub => {
      const responses = sub.responses || {};
      for (const [key, value] of Object.entries(responses)) {
        if (typeof value === 'string' && value.trim()) {
          // Detect category patterns:
          // 1. Starts with number followed by dot/underscore (like "7._महिला_शक्ति_सम्मान")
          // 2. Contains award/सम्मान keywords
          // 3. Key contains category/award
          const valueTrim = value.trim();
          const keyLower = key.toLowerCase();
          
          // Pattern 1: Numbered categories like "7._महिला_शक्ति_सम्मान"
          if (/^\d+[._]/.test(valueTrim)) {
            categorySet.add(valueTrim);
            continue;
          }
          
          // Pattern 2: Contains award keywords
          if (valueTrim.includes('सम्मान') || valueTrim.includes('award') || 
              valueTrim.includes('पुरस्कार') || valueTrim.includes('रत्न')) {
            categorySet.add(valueTrim);
            continue;
          }
          
          // Pattern 3: Key suggests category
          if (keyLower.includes('category') || keyLower.includes('award')) {
            categorySet.add(valueTrim);
          }
        }
      }
    });
    setCategories(Array.from(categorySet).sort());
  };

  const getCategoryFromSubmission = (submission) => {
    const responses = submission.responses || {};
    
    for (const [key, value] of Object.entries(responses)) {
      if (typeof value === 'string' && value.trim()) {
        const valueTrim = value.trim();
        const keyLower = key.toLowerCase();
        
        // Match the same patterns as extractCategories
        if (/^\d+[._]/.test(valueTrim) || 
            valueTrim.includes('सम्मान') || 
            valueTrim.includes('award') ||
            valueTrim.includes('पुरस्कार') ||
            valueTrim.includes('रत्न') ||
            keyLower.includes('category') || 
            keyLower.includes('award')) {
          return valueTrim;
        }
      }
    }
    
    return null;
  };

  const loadAllSubmissions = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/forms/submissions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
      extractCategories(response.data);
      calculateStats(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const loadEventSubmissions = async (eventId) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/forms/submissions/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data.submissions);
      extractCategories(response.data.submissions);
      setStats({
        total: response.data.total,
        paid: response.data.paid_count,
        pending: response.data.pending_count
      });
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const sendBulkNotification = async () => {
    if (!notificationData.subject || !notificationData.message) {
      toast.error('Please enter subject and message');
      return;
    }
    
    if (selectedEvent === 'all') {
      toast.error('Please select a specific event first');
      return;
    }
    
    setSendingNotification(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/notifications/bulk`, {
        event_id: selectedEvent,
        recipient_type: 'nominees',
        subject: notificationData.subject,
        message: notificationData.message,
        filter_category: categoryFilter !== 'all' ? categoryFilter : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowNotificationDialog(false);
      setNotificationData({ subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setSendingNotification(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      paid: data.filter(s => s.payment_status === 'paid').length,
      pending: data.filter(s => s.payment_status === 'pending').length
    });
  };

  const updatePaymentStatus = async (submissionId, status) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/forms/submissions/${submissionId}/payment?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payment marked as ${status}`);
      
      // Refresh data
      if (selectedEvent && selectedEvent !== 'all') {
        loadEventSubmissions(selectedEvent);
      } else {
        loadAllSubmissions();
      }
      setViewingSubmission(null);
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleExport = async () => {
    if (!selectedEvent || selectedEvent === 'all') {
      toast.error('Please select an event to export');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/forms/submissions/event/${selectedEvent}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `registrations_${selectedEvent}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/forms/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission deleted');
      if (selectedEvent && selectedEvent !== 'all') {
        loadEventSubmissions(selectedEvent);
      } else {
        loadAllSubmissions();
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleResendVotingLink = async (submissionId, userEmail) => {
    setResendingId(submissionId);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/forms/submissions/${submissionId}/resend-voting-link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Voting link sent to ${userEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend voting link');
    } finally {
      setResendingId(null);
    }
  };

  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setEditFormData({
      user_name: submission.user_name || '',
      user_email: submission.user_email || '',
      user_phone: submission.user_phone || '',
      vote_count: submission.vote_count || 0,
      responses: { ...submission.responses } || {}
    });
    
    // Get form fields for this submission
    const event = events.find(e => e.event_id === submission.event_id);
    if (event?.registration_form_id && forms[event.registration_form_id]) {
      const form = forms[event.registration_form_id];
      setEditFormFields(form.fields || []);
    } else {
      setEditFormFields([]);
    }
  };
  
  // Helper function to get field label from field ID
  const getFieldLabel = (fieldId, fieldIndex = -1) => {
    // First, try to match by position if we have form fields
    // Form response keys come in order, so we can use the index
    if (fieldIndex >= 0 && editFormFields.length > fieldIndex) {
      const field = editFormFields[fieldIndex];
      if (field?.label) {
        return field.label;
      }
    }
    
    // Try exact match with field ID
    let field = editFormFields.find(f => f.id && f.id === fieldId);
    
    // If not found and field has an ID property, try partial matches
    if (!field) {
      // Extract the unique part from fieldId like "iv5zj39n" from "field_1768027566335_iv5zj39n"
      const parts = fieldId.split('_');
      const uniquePart = parts[parts.length - 1]; // Get last part
      field = editFormFields.find(f => f.id && f.id.includes(uniquePart));
    }
    
    if (field?.label) {
      return field.label;
    }
    
    // Common field name mappings based on typical form fields
    const commonLabels = {
      'name': 'Name',
      'full_name': 'Full Name',
      'email': 'Email',
      'phone': 'Phone',
      'mobile': 'Mobile Number',
      'photo': 'Photo',
      'image': 'Image',
      'picture': 'Picture',
      'category': 'Category',
      'description': 'Description',
      'address': 'Address',
      'dob': 'Date of Birth',
      'date': 'Date',
      'profession': 'Profession',
      'professional': 'Professional Details',
      'organization': 'Organization',
      'designation': 'Designation',
      'achievement': 'Achievement',
      'message': 'Message',
      'award': 'Award Category',
    };
    
    // Check if fieldId contains any common field name
    const fieldIdLower = fieldId.toLowerCase();
    for (const [key, label] of Object.entries(commonLabels)) {
      if (fieldIdLower.includes(key)) {
        return label;
      }
    }
    
    // Try to get label from form fields by index position
    // Response keys usually maintain order as fields were added
    const responseKeys = Object.keys(editFormData.responses || {});
    const keyIndex = responseKeys.indexOf(fieldId);
    if (keyIndex !== -1 && editFormFields.length > keyIndex) {
      const fieldByPosition = editFormFields[keyIndex];
      if (fieldByPosition?.label) {
        return fieldByPosition.label;
      }
    }
    
    // Fallback: try to make the ID more readable
    let label = fieldId;
    // Remove the "field_timestamp_" prefix if present
    if (label.startsWith('field_')) {
      const match = label.match(/^field_\d+_(.+)$/);
      if (match) {
        label = match[1];
      }
    }
    
    // If the remaining label is still a random string, use position-based naming
    if (/^[a-zA-Z0-9]{6,10}$/.test(label) && !/[aeiou]{2,}/i.test(label)) {
      // Use position-based default names
      const index = responseKeys.indexOf(fieldId);
      if (index !== -1) {
        const defaultNames = ['Full Name', 'Email Address', 'Phone Number', 'Category', 'Professional Details', 'About/Description', 'Date of Birth', 'Address', 'Photo/Image', 'Additional Info'];
        if (index < defaultNames.length) {
          return defaultNames[index];
        }
        return `Field ${index + 1}`;
      }
      return 'Field';
    }
    
    // Replace underscores with spaces and capitalize
    return label.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Helper function to check if a field is an image/file upload field
  const isImageField = (fieldId, value) => {
    // Check by field type from form definition (try multiple matching strategies)
    let field = editFormFields.find(f => f.id === fieldId);
    if (!field) {
      field = editFormFields.find(f => f.id && (f.id.includes(fieldId) || f.id.endsWith(`_${fieldId}`)));
    }
    
    if (field?.type === 'file' || field?.type === 'image') {
      return true;
    }
    // Check by field label
    const label = (field?.label || fieldId).toLowerCase();
    if (label.includes('photo') || label.includes('image') || label.includes('picture') || label.includes('upload') || label.includes('चित्र') || label.includes('फोटो')) {
      return true;
    }
    // Check by value - if it looks like an image URL
    if (typeof value === 'string') {
      if (value.includes('/uploads/') || value.includes('/api/files/')) {
        return true;
      }
      // Check for image file extensions
      if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(value)) {
        return true;
      }
    }
    return false;
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditResponseChange = (key, value) => {
    setEditFormData(prev => ({
      ...prev,
      responses: { ...prev.responses, [key]: value }
    }));
  };

  const handleImageUpload = async (fieldKey, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [fieldKey]: true }));
    
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const url = response.data.url;
      handleEditResponseChange(fieldKey, url);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSubmission) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/forms/submissions/${editingSubmission.submission_id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Submission updated successfully!');
      setEditingSubmission(null);
      
      // Refresh data
      if (selectedEvent && selectedEvent !== 'all') {
        loadEventSubmissions(selectedEvent);
      } else {
        loadAllSubmissions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update submission');
    } finally {
      setSaving(false);
    }
  };

  const getPaymentBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">N/A</Badge>;
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = !searchTerm || 
      sub.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.user_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPayment = paymentFilter === 'all' || sub.payment_status === paymentFilter;
    const matchesCategory = categoryFilter === 'all' || getCategoryFromSubmission(sub) === categoryFilter;
    return matchesSearch && matchesPayment && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-form-responses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Form Responses</h1>
          <p className="text-sm text-muted-foreground">View and manage event registrations (nominees)</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowNotificationDialog(true)}
            disabled={selectedEvent === 'all'}
            variant="outline"
            data-testid="send-notification-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            Send Notification
          </Button>
          <Button
            onClick={handleExport}
            disabled={selectedEvent === 'all'}
            style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Registrations</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.paid}</p>
              <p className="text-sm text-muted-foreground">Paid</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-muted-foreground">Pending Payment</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-submissions"
            />
          </div>
        </div>
        <Select value={selectedEvent} onValueChange={setSelectedEvent}>
          <SelectTrigger className="w-full sm:w-[250px]" data-testid="event-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map((event) => (
              <SelectItem key={event.event_id} value={event.event_id}>
                {event.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="payment-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Payment status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="not_required">N/A</SelectItem>
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Submissions Table */}
      <Card>
        <CardContent className="p-0">
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No submissions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.map((sub) => (
                    <TableRow key={sub.submission_id} data-testid={`submission-row-${sub.submission_id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {extractPhoto(sub.responses) && (
                            <img 
                              src={getImageUrl(extractPhoto(sub.responses))} 
                              alt={sub.user_name}
                              className="w-8 h-8 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          {sub.user_name}
                        </div>
                      </TableCell>
                      <TableCell>{sub.user_email}</TableCell>
                      <TableCell>
                        {extractCategory(sub.responses) ? (
                          <Badge variant="outline" className="text-xs max-w-[200px] truncate">
                            {extractCategory(sub.responses)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {sub.event_title || 'Unknown'}
                          {pollingEventIds.has(sub.event_id) && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                              <Vote className="w-3 h-3 mr-1" />
                              Voting
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(sub.created_at)}</TableCell>
                      <TableCell>{getPaymentBadge(sub.payment_status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingSubmission(sub)}
                            data-testid={`view-submission-${sub.submission_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => handleEditSubmission(sub)}
                            data-testid={`edit-submission-${sub.submission_id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {pollingEventIds.has(sub.event_id) && sub.user_email && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                              onClick={() => handleResendVotingLink(sub.submission_id, sub.user_email)}
                              disabled={resendingId === sub.submission_id}
                              data-testid={`resend-voting-link-${sub.submission_id}`}
                            >
                              {resendingId === sub.submission_id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full" />
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleDelete(sub.submission_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Submission Dialog */}
      <Dialog open={!!viewingSubmission} onOpenChange={() => setViewingSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Registration Details</span>
              <Button 
                size="sm" 
                variant="outline"
                className="text-blue-600 border-blue-300"
                onClick={() => {
                  handleEditSubmission(viewingSubmission);
                  setViewingSubmission(null);
                }}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
            </DialogTitle>
          </DialogHeader>

          {viewingSubmission && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{viewingSubmission.user_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{viewingSubmission.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{viewingSubmission.user_phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Registration Date</p>
                  <p className="font-medium">{formatDateTime(viewingSubmission.created_at)}</p>
                </div>
              </div>

              {/* Category Display */}
              {extractCategory(viewingSubmission.responses) && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-700 mb-1">Award Category</p>
                  <p className="font-semibold text-amber-900">{extractCategory(viewingSubmission.responses)}</p>
                </div>
              )}

              {/* Nominee Photo Display */}
              {extractPhoto(viewingSubmission.responses) && (
                <div className="p-4 rounded-lg bg-slate-50 border">
                  <p className="text-sm text-muted-foreground mb-2">Nominee Photo</p>
                  <img
                    src={getImageUrl(extractPhoto(viewingSubmission.responses))}
                    alt={viewingSubmission.user_name}
                    className="max-w-[200px] max-h-[200px] object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => window.open(getImageUrl(extractPhoto(viewingSubmission.responses)), '_blank')}
                    onError={(e) => { 
                      e.target.src = ''; 
                      e.target.alt = 'Image not available'; 
                      e.target.className = 'hidden';
                    }}
                  />
                </div>
              )}

              {/* Voting Link Section - Show for polling-enabled events */}
              {pollingEventIds.has(viewingSubmission.event_id) && (
                <div className="p-4 rounded-lg bg-indigo-50 border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Vote className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium text-indigo-900">Voting Enabled Event</p>
                        <p className="text-sm text-indigo-700">This participant can receive votes</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => handleResendVotingLink(viewingSubmission.submission_id, viewingSubmission.user_email)}
                      disabled={resendingId === viewingSubmission.submission_id || !viewingSubmission.user_email}
                      data-testid="resend-voting-link-dialog-btn"
                    >
                      {resendingId === viewingSubmission.submission_id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Resend Voting Link
                    </Button>
                  </div>
                  <p className="text-xs text-indigo-600 mt-2">
                    Voting URL: {window.location.origin}/vote/{viewingSubmission.submission_id}
                  </p>
                </div>
              )}

              {/* Form Responses */}
              {viewingSubmission.responses && Object.keys(viewingSubmission.responses).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Form Responses</h4>
                  <div className="space-y-4 bg-slate-50 rounded-lg p-4">
                    {Object.entries(viewingSubmission.responses).map(([key, value]) => {
                      // Check if value is an image URL
                      const isImageUrl = typeof value === 'string' && 
                        (value.includes('/uploads/') || value.includes('/api/files/')) &&
                        (value.match(/\.(jpg|jpeg|png|gif|webp)$/i) || value.includes('/uploads/'));
                      
                      // Check if value is any file URL
                      const isFileUrl = typeof value === 'string' && 
                        (value.includes('/uploads/') || value.includes('/api/files/'));
                      
                      return (
                        <div key={key} className="flex flex-col">
                          <span className="text-sm text-muted-foreground capitalize mb-1">{key.replace(/_/g, ' ')}</span>
                          {isImageUrl ? (
                            <div className="mt-1">
                              <img
                                src={getImageUrl(value)}
                                alt={key}
                                className="max-w-[200px] max-h-[200px] object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(getImageUrl(value), '_blank')}
                              />
                              <p className="text-xs text-muted-foreground mt-1">Click to view full size</p>
                            </div>
                          ) : isFileUrl ? (
                            <a 
                              href={getImageUrl(value)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline font-medium flex items-center gap-1"
                            >
                              <FileText className="w-4 h-4" />
                              View uploaded file
                            </a>
                          ) : (
                            <span className="font-medium">
                              {Array.isArray(value) ? value.join(', ') : String(value) || '-'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Payment Section */}
              {viewingSubmission.payment_status !== 'not_required' && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Payment Details</h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p className="font-medium text-lg">₹{viewingSubmission.payment_amount || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      {getPaymentBadge(viewingSubmission.payment_status)}
                    </div>
                  </div>

                  {viewingSubmission.payment_proof_url && (
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground mb-2">Payment Proof</p>
                      <img
                        src={getImageUrl(viewingSubmission.payment_proof_url)}
                        alt="Payment Proof"
                        className="max-w-full h-auto rounded-lg border cursor-pointer"
                        onClick={() => window.open(getImageUrl(viewingSubmission.payment_proof_url), '_blank')}
                      />
                    </div>
                  )}

                  {viewingSubmission.payment_status === 'pending' && (
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => updatePaymentStatus(viewingSubmission.submission_id, 'paid')}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="approve-payment-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Paid
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-300"
                        onClick={() => updatePaymentStatus(viewingSubmission.submission_id, 'rejected')}
                        data-testid="reject-payment-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Submission Dialog */}
      <Dialog open={!!editingSubmission} onOpenChange={() => setEditingSubmission(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Nominee / Registration
            </DialogTitle>
          </DialogHeader>

          {editingSubmission && (
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="responses">Form Responses</TabsTrigger>
              </TabsList>

              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 py-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={editFormData.user_name}
                      onChange={(e) => handleEditFieldChange('user_name', e.target.value)}
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={editFormData.user_email}
                      onChange={(e) => handleEditFieldChange('user_email', e.target.value)}
                      placeholder="Email Address"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editFormData.user_phone}
                      onChange={(e) => handleEditFieldChange('user_phone', e.target.value)}
                      placeholder="Phone Number"
                    />
                  </div>
                  {pollingEventIds.has(editingSubmission.event_id) && (
                    <div>
                      <Label>Vote Count</Label>
                      <Input
                        type="number"
                        value={editFormData.vote_count}
                        onChange={(e) => handleEditFieldChange('vote_count', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        min="0"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Manually set vote count</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Form Responses Tab */}
              <TabsContent value="responses" className="space-y-4 py-4">
                {editFormData.responses && Object.keys(editFormData.responses).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(editFormData.responses).map(([key, value], index) => {
                      const fieldLabel = getFieldLabel(key, index);
                      const isImage = isImageField(key, value);
                      
                      return (
                        <div key={key} className="border rounded-lg p-4">
                          <Label className="mb-2 block font-medium">{fieldLabel}</Label>
                          
                          {isImage ? (
                            <div className="space-y-3">
                              {value && (
                                <div className="relative inline-block">
                                  <img
                                    src={getImageUrl(value)}
                                    alt={fieldLabel}
                                    className="max-w-[180px] max-h-[180px] object-cover rounded-lg border shadow-sm"
                                    onError={(e) => { 
                                      console.error('Image failed to load:', value, getImageUrl(value));
                                      e.target.onerror = null;
                                      e.target.src = '';
                                      e.target.alt = 'Failed to load image';
                                      e.target.className = 'hidden';
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                                    onClick={() => handleEditResponseChange(key, '')}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                              <div className="flex flex-col gap-2">
                                <div className="flex gap-2 items-center">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    ref={el => fileInputRefs.current[key] = el}
                                    onChange={(e) => handleImageUpload(key, e.target.files?.[0])}
                                  />
                                  <Button
                                    variant="outline"
                                    onClick={() => fileInputRefs.current[key]?.click()}
                                    disabled={uploading[key]}
                                    className="flex-shrink-0"
                                  >
                                    {uploading[key] ? (
                                      <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full mr-2" />
                                    ) : (
                                      <Upload className="w-4 h-4 mr-2" />
                                    )}
                                    {value ? 'Change Image' : 'Upload Image'}
                                  </Button>
                                  {value && (
                                    <a 
                                      href={getImageUrl(value)} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-sm text-blue-600 hover:underline truncate max-w-[200px]"
                                    >
                                      View Image
                                    </a>
                                  )}
                                </div>
                                {value && (
                                  <Input
                                    value={value}
                                    onChange={(e) => handleEditResponseChange(key, e.target.value)}
                                    placeholder="Image URL"
                                    className="text-xs"
                                  />
                                )}
                              </div>
                            </div>
                          ) : typeof value === 'string' && value.length > 100 ? (
                            <Textarea
                              value={value}
                              onChange={(e) => handleEditResponseChange(key, e.target.value)}
                              rows={4}
                              placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                            />
                          ) : (
                            <Input
                              value={typeof value === 'string' ? value : JSON.stringify(value)}
                              onChange={(e) => handleEditResponseChange(key, e.target.value)}
                              placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                            />
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Add Photo Field if not present */}
                    {!Object.keys(editFormData.responses).some(key => isImageField(key, editFormData.responses[key])) && (
                      <div className="border rounded-lg p-4 border-dashed border-blue-300 bg-blue-50/50">
                        <Label className="mb-2 block font-medium text-blue-700">Add Nominee Photo</Label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => fileInputRefs.current['nominee_photo'] = el}
                            onChange={(e) => handleImageUpload('nominee_photo', e.target.files?.[0])}
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRefs.current['nominee_photo']?.click()}
                            disabled={uploading['nominee_photo']}
                            className="border-blue-300 text-blue-700 hover:bg-blue-100"
                          >
                            {uploading['nominee_photo'] ? (
                              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-2" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Upload Photo
                          </Button>
                          <span className="text-sm text-blue-600">No photo field found - add one here</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No form responses to edit</p>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditingSubmission(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              disabled={saving}
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Notification Dialog */}
      <Dialog open={showNotificationDialog} onOpenChange={setShowNotificationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Send Notification to Nominees
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800">Recipients:</p>
              <p className="text-blue-700">
                {filteredSubmissions.length} nominee(s) 
                {categoryFilter !== 'all' && ` in "${categoryFilter}"`}
                {selectedEvent !== 'all' && ` for selected event`}
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Notification will be sent via channels configured for this event (Email/SMS/WhatsApp)
              </p>
            </div>
            
            <div>
              <Label>Subject</Label>
              <Input
                value={notificationData.subject}
                onChange={(e) => setNotificationData({ ...notificationData, subject: e.target.value })}
                placeholder="Enter notification subject"
              />
            </div>
            
            <div>
              <Label>Message</Label>
              <Textarea
                value={notificationData.message}
                onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                placeholder="Enter your message to nominees..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotificationDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={sendBulkNotification}
              disabled={sendingNotification || !notificationData.subject || !notificationData.message}
              style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
            >
              {sendingNotification ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {filteredSubmissions.length} Nominees
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFormResponsesPage;
